/* eslint import/no-extraneous-dependencies: off*/
import { createSlice } from '@reduxjs/toolkit';
import history from '@history';
import _ from '@lodash';
import AccessManagementService from 'app/admin/user/service/AccessManagementService';
import UserLoginService from 'app/admin/user/service/UserLoginService';
import { setInitialSettings, setDefaultSettings } from 'app/store/fuse/settingsSlice';
import { changeLanguage } from 'app/store/i18nSlice';

const safeArr = value => (Array.isArray(value) ? value : []);

const normalizeRole = value => (value == null ? null : String(value).toLowerCase());

const getRoleValue = value => {
	if (value == null) return null;
	if (typeof value === 'string') return value;
	if (typeof value === 'object') return value.key ?? value.name ?? value.title ?? null;
	return String(value);
};

const extractClientFromState = stateDto => {
	if (!stateDto || typeof stateDto !== 'object') return null;
	if (stateDto.client && typeof stateDto.client === 'object') return stateDto.client;
	if (stateDto.id != null && (stateDto.email != null || stateDto.name != null)) return stateDto;
	return null;
};

const parseUserSettings = () => {
	try {
		return JSON.parse(localStorage.getItem('userSettings')) || {};
	} catch (e) {
		return {};
	}
};

const convertLoggedInClientToAccessConfig = (client, { userGroups = [], roles = [], role = null, userGroupId = null } = {}) => {
	const normalizedRole = normalizeRole(role) || normalizeRole(getRoleValue(roles[0])) || 'admin';
	const roleList = safeArr(client?.roleList).length
		? safeArr(client.roleList)
		: [{ "servicePermissionList": [], "contentPermissionList": [], "isEnabled": true, "key": normalizedRole, "title": normalizedRole, "isSuperAdmin": true, "id": 1 }];

	return {
		"user": {
			"id": client?.id ?? null,
			"name": client?.name ?? null,
			"email": client?.email ?? null,
			"profilePicture": client?.profilePicture ?? null,
			"currentLocale": client?.currentLocale ?? "en",
			"whatIfGroupList": [],
			"userGroups": safeArr(userGroups),
			"roles": safeArr(roles),
			"userGroupId": userGroupId ?? null,
			"role": normalizedRole
		},
		"roleList": roleList,
		"serviceList": [
			'Admin',
			'UserProfile',
			'UserAccount',
			'UserRole',
		],
		"serviceLevelMap": {
			"Admin": "WriteRead",
			"UserProfile": "WriteRead",
			"UserAccount": "WriteRead",
			"UserRole": "WriteRead",
		},
		"settingsMap": null,
		"contentTypeLevelMap": {},
		"accessRole": normalizedRole
	}
};

export const getUserData = () => async dispatch => {
	return new Promise(resolve => {
		const pState = AccessManagementService.getState ? AccessManagementService.getState().catch(() => null) : Promise.resolve(null);
		const pRole = AccessManagementService.getRole ? AccessManagementService.getRole().catch(() => null) : Promise.resolve(null);

		Promise.all([pState, pRole])
			.then(([stateDto, roleFromEndpoint]) => {
				const stateClient = extractClientFromState(stateDto);

				const finish = client => {
					if (!client) {
						resolve();
						return;
					}

					const userGroups = safeArr(stateDto?.userGroups ?? stateDto?.groups ?? client?.userGroups);
					const roles = safeArr(stateDto?.roles ?? client?.roles);
					const selectedRole = normalizeRole(stateDto?.role) || normalizeRole(roleFromEndpoint) || normalizeRole(getRoleValue(roles[0])) || 'admin';
					const userGroupId = stateDto?.userGroupId ?? stateDto?.groupId ?? client?.userGroupId ?? client?.activeUserGroupId ?? null;
					const usersSettings = parseUserSettings();
					const accessConfig = convertLoggedInClientToAccessConfig(client, { userGroups, roles, role: selectedRole, userGroupId });

					dispatch(
						setUserData({
							role: accessConfig.serviceList,
							accessRole: selectedRole,
							serviceList: accessConfig.serviceList,
							data: { ...accessConfig.user, ...{ settings: usersSettings || {} } },
							roleList: accessConfig.roleList,
							isSuperAdmin: accessConfig.isSuperAdmin,
							accessType: accessConfig.accessType,
							settingMap: accessConfig.settingMap,
							serviceLevelMap: accessConfig.serviceLevelMap,
							userGroups,
							roles
						})
					);
					resolve();
				};

				if (stateClient) {
					finish(stateClient);
					return;
				}

				AccessManagementService.getLoggedInClient()
					.then(finish)
					.catch(() => resolve());
			})
			.catch(() => resolve());
	});
};

// export const getUserData = () => async (dispatch, getState) => {
// 	return new Promise((resolve, reject) => {
// 		AccessManagementService.getAccessConfig().then(accessConfig => {
// 			const usersSettings = JSON.parse(localStorage.getItem('userSettings'));
// 			dispatch(
// 				setUserData({
// 					role: accessConfig.serviceList,
// 					data: { ...accessConfig.user, ...{ settings: usersSettings || {} } },
// 					roleList: accessConfig.roleList,
// 					isSuperAdmin: accessConfig.isSuperAdmin,
// 					accessType: accessConfig.accessType
// 				})
// 			);
// 			resolve();
// 		});
// 	});
// };

export const setUserData = user => async dispatch => {
	/*
		You can redirect the logged-in user to a specific route depending on his role
	*/
	if (!_.isUndefined(user.data.currentLocale) && user.data.currentLocale !== null) dispatch(changeLanguage(user.data.currentLocale));

	history.location.state = {
		redirectUrl: user.redirectUrl // for example 'apps/academy'
	};

	/*
		Set User Settings
	 */
	dispatch(setDefaultSettings(user.data.settings));

	dispatch(setUser(user));
};

export const logoutUser = () => async (dispatch, getState) => {
	const { user } = getState().auth;

	if (!user.role || user.role.length === 0) {
		// is guest
		return null;
	}

	history.push({
		pathname: '#'
	});

	if (AccessManagementService.logout) AccessManagementService.logout();
	else UserLoginService.logout();
	dispatch(setInitialSettings());
	return dispatch(userLoggedOut());
};

export const logoutUserSilent = () => async dispatch => {
	try {
		if (AccessManagementService.logout) await Promise.resolve(AccessManagementService.logout());
		else await Promise.resolve(UserLoginService.logout());
	} catch (e) {}

	dispatch(setInitialSettings());
	return dispatch(userLoggedOut());
};

export const updateUserSettings = settings => async (dispatch, getState) => {
	const oldUser = getState().auth.user;
	const user = _.merge({}, oldUser, { data: { settings } });

	dispatch(updateUserData(user));

	return dispatch(setUserData(user));
};

export const updateUserShortcuts = shortcuts => async (dispatch, getState) => {
	/*const { user } = getState().auth;
	const newUser = {
		...user,
		data: {
			...user.data,
			shortcuts
		}
	};

	dispatch(updateUserData(user));

	return dispatch(setUserData(newUser));*/
};

export const updateUserData = user => async (dispatch, getState) => {
	if (!user.role || user.role.length === 0) {
		// is guest
		return;
	}
	localStorage.setItem('userSettings', JSON.stringify(user.data.settings));

	/*switch (user.from) {
		case 'firebase': {
			firebaseService
				.updateUserData(user)
				.then(() => {
					dispatch(showMessage({ message: 'User data saved to firebase' }));
				})
				.catch(error => {
					dispatch(showMessage({ message: error.message }));
				});
			break;
		}
		case 'auth0': {
			auth0Service
				.updateUserData({
					settings: user.data.settings,
					shortcuts: user.data.shortcuts
				})
				.then(() => {
					dispatch(showMessage({ message: 'User data saved to auth0' }));
				})
				.catch(error => {
					dispatch(showMessage({ message: error.message }));
				});
			break;
		}
		default: {
			jwtService
				.updateUserData(user)
				.then(() => {
					dispatch(showMessage({ message: 'User data saved with api' }));
				})
				.catch(error => {
					dispatch(showMessage({ message: error.message }));
				});
			break;
		}
	}*/
};

const initialState = {
	role: [],
	data: {
		name: 'Guest'
	}
};

const userSlice = createSlice({
	name: 'auth/user',
	initialState,
	reducers: {
		setUser: (state, action) => action.payload,
		userLoggedOut: (state, action) => initialState
	},
	extraReducers: {}
});

export const { setUser, userLoggedOut } = userSlice.actions;

export default userSlice.reducer;
